import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { App, Card, Typography, Button, Descriptions, Tooltip } from 'antd'
import { ContainerOutlined, EyeOutlined, ScheduleOutlined, CommentOutlined } from '@ant-design/icons'
import { useIntl } from 'react-intl'
import Api from '@vacationtracker/shared/services/api'
import IntlMessages from '@vacationtracker/shared/components/utils/IntlMessages'
import { FeedbackModal } from '@vacationtracker/shared/components/feedback-modal'
import { FrontendUrls } from '../../types/urls'
import { useAppSelector } from '../../store/hooks'
import { selectUserIdSlice } from '../../store/user-id-slice'
import { selectAuthCompanySlice } from '../../store/auth-company-slice'
import { SubscriptionPlanEnum } from '@vacationtracker/shared/types/company'
import FreemiumPricingSimpleTooltip from '../../components/freemium-pricing-simple-tooltip'

const { Paragraph } = Typography

const ReportsPage = () => {
  const { notification } = App.useApp()
  const { formatMessage } = useIntl()

  const toggleFeedbackModal = () => setShowFeedbackModal(!showFeedbackModal)
  const [showFeedbackModal, setShowFeedbackModal] = useState(false)
  const { authCompany } = useAppSelector(selectAuthCompanySlice)
  const { userId } = useAppSelector(selectUserIdSlice)

  const sendFeedback = async (data) => { 
    await Api.post('/core/event', {
      eventGroup: 'FEEDBACK',
      eventType: 'USER_FEEDBACK',
      userId,
      ...data,
    })
    setShowFeedbackModal(false)
    notification.success({
      message: formatMessage({ id: 'components.feedbackModal.successMessage' }),
      icon: <CommentOutlined />,
    })
  }

  return (
    <div className='main-content'>
      <div className="main-content-header">
        <div className="main-content-header-title">
          <IntlMessages id="sidebar.reports" />
        </div>
      </div>
      <div className="main-content-body" id="vt-cards">
        <Paragraph className='vt-cards-intro'><IntlMessages id="components.reports.intro" /></Paragraph>
        <div className='vt-cards-container'>
          <ReportDescription
            header={<IntlMessages id="export.leaveHistoryReport" />}
            content={<IntlMessages id="export.leaveHistoryReportDescription" />}
            path={`${FrontendUrls.reports}/leave-history-report`}
            actionText={<IntlMessages id="components.reports.generate"/>}
            actionIcon={<ContainerOutlined />}
          />
          <ReportDescription
            header={<IntlMessages id="export.leaveBalanceReport" />}
            content={<IntlMessages id="export.leaveBalanceReportDescription" />}
            path={`${FrontendUrls.reports}/leave-balance-report`}
            actionText={<IntlMessages id="components.reports.generate"/>}
            actionIcon={<ContainerOutlined />}
          />
          <FreemiumPricingSimpleTooltip 
            isFreemiumPlan={authCompany?.plan === SubscriptionPlanEnum.freemium}
            featureName="monthly_balance_report"
            featureCategory="reports"
          >
            <ReportDescription
              header={<IntlMessages id="export.monthlyLeaveBalanceReport" />}
              content={<IntlMessages id="export.monthlyLeaveBalanceReportDescription" />}
              // eslint-disable-next-line max-len
              path={`${FrontendUrls.reports}/leave-balance-monthly-report`}
              actionText={<IntlMessages id="components.reports.generate"/>}
              actionIcon={<ContainerOutlined />}
              disabled={authCompany?.plan === SubscriptionPlanEnum.freemium}
            />
          </FreemiumPricingSimpleTooltip>
          <FreemiumPricingSimpleTooltip 
            isFreemiumPlan={authCompany?.plan === SubscriptionPlanEnum.freemium}
            featureName="company_logs"
            featureCategory="reports"
          >
            <ReportDescription
              header={<IntlMessages id="components.reports.companyLogs" />}
              content={<IntlMessages id="components.reports.companyLogsDescription" />}
              path={FrontendUrls.companyLogs}
              actionText={<IntlMessages id="app.view"/>}
              actionIcon={<EyeOutlined />}
              disabled={authCompany?.plan === SubscriptionPlanEnum.freemium}
            />
          </FreemiumPricingSimpleTooltip>
          <FreemiumPricingSimpleTooltip 
            isFreemiumPlan={authCompany?.plan === SubscriptionPlanEnum.freemium} 
            onCorePlan={false}
            featureName="scheduled_reports"
            featureCategory="reports"
          >
            <ReportDescription
              header={<IntlMessages id="sidebar.scheduledReports" />}
              content={<IntlMessages id="components.reports.scheduledReportsDescription" />}
              path={FrontendUrls.scheduledReports}
              actionText={<IntlMessages id="scheduledReportForm.saveReport"/>}
              actionIcon={<ScheduleOutlined />}
              disabled={authCompany?.plan === 'Core' || authCompany?.plan === SubscriptionPlanEnum.freemium}
            />
          </FreemiumPricingSimpleTooltip>
        </div>
        <Paragraph className='cards-feedback'>
          <IntlMessages id="components.reports.feedback" />
          <Button type='primary' onClick={toggleFeedbackModal}>
            <IntlMessages id='components.feedbackModal.button' />
          </Button>
        </Paragraph>
        <FeedbackModal visible={showFeedbackModal} onCancel={toggleFeedbackModal} onSave={sendFeedback}/>
      </div>
    </div>
  )
}

export default ReportsPage

const ReportDescription = ({ header, content, path, actionText, actionIcon, disabled = false }) => {
  const CardContent = (
    <Card
      className={`vt-card ${disabled ? 'vt-card-disabled' : ''}`}
      hoverable={!disabled}
      key={path}
      actions={[
        <div key={path} data-disabled={disabled} onClick={e => disabled && e.stopPropagation()}>
          {actionIcon}&nbsp;{actionText}
        </div>,
      ]}
      title={header}
    >
      <Descriptions layout="horizontal" size="small" column={1}>
        <Descriptions.Item key="desc">
          {content}
        </Descriptions.Item>
      </Descriptions>
    </Card>
  )

  return disabled ? (
    <div className="cards-feedback">
      {CardContent}
    </div>
  ) : (
    <Link to={path} className="cards-feedback">
      {CardContent}
    </Link>
  )
}